/* Source: /app/hooks/usePlayground.cl.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { useCallback, useRef } from "react";
import { PythonThread } from "../lib/python_thread.js";
import { useState } from "@jac/runtime";
import { useEffect } from "@jac/runtime";
function usePlayground(props) {
  const [theme, setTheme] = useState("dark");
  const [mode, setMode] = useState("jac");
  let editorRef = useRef(null);
  const [jacCode, setJacCode] = useState("");
  const [pythonCode, setPythonCode] = useState("");
  const [outputCode, setOutputCode] = useState("");
  const [output, setOutput] = useState("");
  const [errors, setErrors] = useState("");
  const [logs, setLogs] = useState("");
  let [examples, setExamples] = useState((props.examples || []));
  const [pythonThread, setPythonThread] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isDebugging, setIsDebugging] = useState(false);
  const [breakpoints, setBreakpoints] = useState([]);
  const [activityBarCollapsed, setActivityBarCollapsed] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [rightPanelSection, setRightPanelSection] = useState("examples");
  const [consoleOpen, setConsoleOpen] = useState(true);
  const [consoleHeight, setConsoleHeight] = useState(200);
  const [activePanel, setActivePanel] = useState("code");
  const [graphData, setGraphData] = useState(null);
  const [envReady, setEnvReady] = useState((props.envReady || false));
  let codeValue = (((mode === "py2jac") && pythonCode) || jacCode);
  let editorTheme = (((theme === "dark") && "vs-dark") || "vs-light");
  let problemsBadge = (((errors !== "") && errors.split("\\n").length) || 0);
  useEffect(() => {
    let existingViewport = document.querySelector("meta[name='viewport']");
    if (!existingViewport) {
      let meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
      document.head.appendChild(meta);
    }
  }, []);
  useEffect(() => {
    if ((theme === "dark")) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);
  useEffect(() => {
    console.log("App mounted");
    let newPythonThread = Reflect.construct(PythonThread, [loaded => {
      setEnvReady(loaded);
    }]);
    setPythonThread(newPythonThread);
  }, []);
  useEffect(() => {
    if (pythonThread) {
      console.log("Syncing breakpoints to Python thread:", breakpoints);
      pythonThread.setBreakpoints(breakpoints);
    }
  }, [breakpoints, pythonThread]);
  function handleModeChange(newMode) {
    if ((newMode === "py2jac")) {
      setOutputCode("");
      setJacCode("");
      setPythonCode("");
    }
    setMode(newMode);
    setOutput("");
    setErrors("");
    setOutputCode("");
  }
  function handleToggleTheme() {
    setTheme((((theme === "dark") && "light") || "dark"));
  }
  function handleSelectExample(exampleId) {
    for (const ex of examples) {
      if ((ex["id"] === exampleId)) {
        setJacCode(ex["code"]);
        setMode("jac");
        setLogs(`✓ Loaded example: ${ex["title"]}`);
        setActivePanel("code");
      }
    }
  }
  function runJacCodes(jac_code) {
    pythonThread.callbackBreakHit = lineNumber => {
      console.log(`⚠️ Hit breakpoint at line ${lineNumber}. Execution paused.`);
      editorRef.current.highlightExecutionLine(lineNumber);
    };
    pythonThread.callbackStdout = outputText => {
      setOutput(prev => {
        return (prev + outputText);
      });
    };
    pythonThread.callbackStderr = errorText => {
      setOutput(prev => {
        return (prev + errorText);
      });
    };
    pythonThread.callbackExecEnd = () => {
      setIsRunning(false);
      console.log("Execution ended");
      if ((editorRef.current && editorRef.current.clearExecutionHighlight)) {
        editorRef.current.clearExecutionHighlight();
      }
    };
    pythonThread.callbackJacGraph = graph_str => {
      let graph = JSON.parse(graph_str);
      setGraphData(graph);
    };
    try {
      pythonThread.startExecution(jac_code);
    } catch (__jac_e) {
      if (true) {
        console.error("Error starting Jac code execution:", err);
      } else {
        throw __jac_e;
      }
    }
  }
  function handleBreakpointsChange(breakpoints) {
    setBreakpoints(breakpoints);
    if (pythonThread) {
      setTimeout(() => {
        pythonThread.setBreakpoints(breakpoints);
      }, 100);
    }
  }
  function runPythonCodes(python_code) {
    pythonThread.callbackStdout = outputText => {
      setOutput(prev => {
        return (prev + outputText);
      });
    };
    pythonThread.callbackStderr = errorText => {
      setOutput(prev => {
        return (prev + errorText);
      });
    };
    pythonThread.callbackExecEnd = () => {
      setIsRunning(false);
    };
    try {
      pythonThread.startPythonExecution(python_code);
    } catch (__jac_e) {
      if (true) {
        console.error("Error starting Python code execution:", err);
      } else {
        throw __jac_e;
      }
    }
  }
  function performConversion(code, conversionType) {
    pythonThread.callbackConversionResult = result => {
      setOutputCode(prev => {
        return (prev + result);
      });
      setIsConverting(false);
      setLogs("\u2713 Conversion complete.");
      console.log("Conversion ended");
    };
    try {
      pythonThread.startConversion(conversionType, code);
    } catch (__jac_e) {
      if (true) {
        console.error("Error starting code conversion:", err);
        setIsConverting(false);
      } else {
        throw __jac_e;
      }
    }
  }
  async function handleRun(title) {
    let result = [];
    let graph = [];
    setIsRunning(true);
    setOutput("");
    setErrors("");
    setGraphData(null);
    setActivePanel("output");
    if ((title === "Python Input")) {
      runPythonCodes(pythonCode);
    } else if ((title === "Jac Input")) {
      runJacCodes(jacCode);
    } else if ((title === "Python Output")) {
      runPythonCodes(outputCode);
    } else if ((title === "Jac Output")) {
      runJacCodes(outputCode);
    } else {
      runJacCodes(jacCode);
    }
  }
  async function handleConvert() {
    setIsConverting(true);
    setOutput("");
    setOutputCode("");
    if ((mode === "jac2py")) {
      setLogs("Converting Jac to Python...");
      performConversion(jacCode, "jac2py");
    } else {
      setLogs("Converting Python to Jac...");
      performConversion(pythonCode, "py2jac");
    }
  }
  let handleDebugAction = useCallback(action => {
    if ((action === "continue")) {
      console.log("continue");
      pythonThread.continueExecution();
    } else if ((action === "stepOver")) {
      console.log("stepOver");
      pythonThread.stepOver();
    } else if ((action === "stepInto")) {
      console.log("stepInto");
      pythonThread.stepInto();
    } else if ((action === "stepOut")) {
      console.log("stepOut");
      pythonThread.stepOut();
    } else if ((action === "restart")) {
      console.log("restart");
      editorRef.current.clearExecutionHighlight();
      if (isRunning) {
        pythonThread.terminate();
        Reflect.construct(Promise, [resolve => {
          setTimeout(resolve, 1000);
        }]);
      }
      setOutput("");
      pythonThread.startExecution(jacCode);
      setIsRunning(true);
    } else if ((action === "stop")) {
      console.log("stop");
      pythonThread.terminate();
    }
  }, [breakpoints, pythonThread, jacCode]);
  function handleReset() {
    setJacCode("");
    setPythonCode("");
    setOutputCode("");
    setOutput("");
    setErrors("");
    setLogs("");
  }
  function handleDebug() {
    setIsDebugging(!isDebugging);
    setLogs(((isDebugging && "Debug mode disabled.") || "Debug mode enabled. Set breakpoints in the editor."));
  }
  function handleClearOutput() {
    setOutput("");
  }
  function handleClearErrors() {
    setErrors("");
  }
  function handleClearLogs() {
    setLogs("");
  }
  function handleJacCodeChange(v) {
    setJacCode(v);
  }
  function handlePythonCodeChange(v) {
    setPythonCode(v);
  }
  function handleToggleActivityBar() {
    setActivityBarCollapsed(!activityBarCollapsed);
  }
  function handleToggleRightPanel() {
    setRightPanelOpen(!rightPanelOpen);
  }
  function handleToggleConsole() {
    setConsoleOpen(!consoleOpen);
  }
  function handleNavigateToCode() {
    setActivePanel("code");
  }
  function handleNavigateToOutput() {
    setActivePanel("output");
  }
  function handleNavigateToGraph() {
    setActivePanel("graph");
  }
  function handleNavigateToExamples() {
    setActivePanel("examples");
  }
  return {"theme": theme, "editorTheme": editorTheme, "onToggleTheme": handleToggleTheme, "mode": mode, "onModeChange": handleModeChange, "jacCode": jacCode, "pythonCode": pythonCode, "outputCode": outputCode, "codeValue": codeValue, "onJacCodeChange": handleJacCodeChange, "onPythonCodeChange": handlePythonCodeChange, "output": output, "errors": errors, "logs": logs, "onClearOutput": handleClearOutput, "onClearErrors": handleClearErrors, "onClearLogs": handleClearLogs, "examples": examples, "setExamples": setExamples, "onSelectExample": handleSelectExample, "isRunning": isRunning, "isConverting": isConverting, "isDebugging": isDebugging, "onRun": handleRun, "onConvert": handleConvert, "onReset": handleReset, "onDebug": handleDebug, "onhandleDebugAction": handleDebugAction, "problemsBadge": problemsBadge, "envReady": envReady, "graphData": graphData, "activityBarCollapsed": activityBarCollapsed, "onToggleActivityBar": handleToggleActivityBar, "rightPanelOpen": rightPanelOpen, "onToggleRightPanel": handleToggleRightPanel, "rightPanelSection": rightPanelSection, "onRightPanelSectionChange": setRightPanelSection, "consoleOpen": consoleOpen, "onToggleConsole": handleToggleConsole, "consoleHeight": consoleHeight, "onConsoleHeightChange": setConsoleHeight, "onBreakpointsChange": handleBreakpointsChange, "activePanel": activePanel, "onPanelChange": setActivePanel, "onNavigateToCode": handleNavigateToCode, "onNavigateToOutput": handleNavigateToOutput, "onNavigateToGraph": handleNavigateToGraph, "onNavigateToExamples": handleNavigateToExamples, "editorRef": editorRef};
}
export {usePlayground};